import { useI18n } from "../../../i18n";
import { Goal } from "./ScenePanels";
import {
  BAND_KEYS, FIELD_N, SHOWCASE_PIECES, SHOWCASE_VISITORS, TREASURE, TWINKLE, bandDone, cheerDone, noteOf, rssiOf, showcaseDone,
} from "../lib/stages";
import type { Band, Cheer, HotCold, Melody, Showcase } from "../lib/stages";

/** 마지막 묶음 판(lib/stages) — 응원 무대 · 작품 전시관 · 음악 무대 · 밴드 무대 · 운동장 보물찾기 */

export function CheerPanel({ s }: { s: Cheer }) {
  const { t } = useI18n();
  const done = cheerDone(s);
  const same = s.a !== null && s.a === s.b;
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-cheer">
      <div className="pf-mb__row">
        <span className="pf-mb__big" data-on={s.a !== null || undefined} data-testid="fs-mb-cheer-a">{s.a ? "🙌" : "🧍"}</span>
        <span className="pf-mb__big" data-on={s.b !== null || undefined} data-testid="fs-mb-cheer-b">{s.b ? "👏" : "🧍"}</span>
      </div>
      <p className="pf-mb__scene-state">
        {t("student.microbit.scene.cheer.state", { a: s.a ? "✓" : "—", b: s.b ? "✓" : "—" })}
        {same ? ` · ${t("student.microbit.scene.cheer.same")}` : ""}
      </p>
      <Goal id="fs-mb-scene-goal" done={done} text={t("student.microbit.scene.cheer.goal")} />
    </div>
  );
}

export function ShowcasePanel({ s }: { s: Showcase }) {
  const { t } = useI18n();
  const full = s.pieces.length >= SHOWCASE_PIECES;
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-showcase">
      <div className="pf-mb__row">
        {Array.from({ length: SHOWCASE_PIECES }, (_, i) => (
          <span key={i} className="pf-mb__big" data-on={i < s.pieces.length || undefined}>{i < s.pieces.length ? "🖼️" : "⬜"}</span>
        ))}
        <span className="pf-mb__big" aria-label={t("student.microbit.scene.showcase.visitors", { n: s.visitors })}>
          {"🧑‍🎨".repeat(Math.min(s.visitors, SHOWCASE_VISITORS)) || "🚪"}{s.visitors > 0 ? " ⭐".repeat(s.visitors) : ""}
        </span>
      </div>
      <p className="pf-mb__scene-state" data-testid="fs-mb-showcase-state">
        {full ? t("student.microbit.scene.showcase.visitors", { n: s.visitors }) : t("student.microbit.scene.showcase.pieces", { n: s.pieces.length, goal: SHOWCASE_PIECES })}
      </p>
      <Goal id="fs-mb-scene-goal" done={showcaseDone(s)} text={t("student.microbit.scene.showcase.goal", { goal: SHOWCASE_VISITORS })} />
    </div>
  );
}

const KEYS = ["C", "D", "E", "F", "G", "A", "B"] as const;

export function MelodyPanel({ s }: { s: Melody }) {
  const { t } = useI18n();
  const done = s.i >= TWINKLE.length;
  const want = TWINKLE[Math.min(s.i, TWINKLE.length - 1)]!;
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-melody">
      <div className="pf-mb__row" role="img" aria-label={t("student.microbit.scene.melody.name")}>
        {KEYS.map((k) => (
          <span key={k} className="pf-mb__chip" data-on={s.last === k || undefined} data-testid={s.last === k ? "fs-mb-melody-lit" : undefined}>{k}</span>
        ))}
      </div>
      <p className="pf-mb__scene-state" data-testid="fs-mb-melody-state">
        {TWINKLE.map((n, i) => (i < s.i ? `${n}✓` : n)).join(" ")}
      </p>
      <p className="pf-mb__scene-state">
        {done ? t("student.microbit.scene.melody.done") : t("student.microbit.scene.melody.next", { note: want })}
        {s.wrong ? ` · ${t("student.microbit.scene.melody.again", { n: s.wrong })}` : ""}
      </p>
      <Goal id="fs-mb-scene-goal" done={done} text={t("student.microbit.scene.melody.goal", { n: Math.min(s.i, TWINKLE.length), goal: TWINKLE.length })} />
    </div>
  );
}

export function BandPanel({ s }: { s: Band }) {
  const { t } = useI18n();
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-band">
      <div className="pf-mb__row pf-mb__row--start">
        {BAND_KEYS.map((k) => {
          const hz = s.notes[k];
          return (
            <span key={k} className="pf-mb__chip" data-on={hz !== undefined || undefined} data-testid={`fs-mb-band-${k}`}>
              {t(`student.microbit.scene.band.key.${k}`)} {hz !== undefined ? `🎵 ${noteOf(hz) ?? "?"}` : "—"}
            </span>
          );
        })}
      </div>
      <Goal id="fs-mb-scene-goal" done={bandDone(s)} text={t("student.microbit.scene.band.goal")} />
    </div>
  );
}

export function HotColdPanel({ s, group }: { s: HotCold; group: number | null }) {
  const { t } = useI18n();
  const rssi = rssiOf(s);
  const heat = rssi >= -55 ? "🔥" : rssi >= -70 ? "🌤️" : "🧊";
  return (
    <div className="pf-mb__scene" data-testid="fs-mb-scene-hotCold">
      <div className="pf-mb__grid" style={{ gridTemplateColumns: `repeat(${FIELD_N}, 1fr)` }} role="img" aria-label={t("student.microbit.scene.hotCold.name")}>
        {Array.from({ length: FIELD_N * FIELD_N }, (_, i) => {
          const r = Math.floor(i / FIELD_N), c = i % FIELD_N;
          const me = s.r === r && s.c === c;
          const gem = s.found && r === TREASURE.r && c === TREASURE.c;
          return <span key={i} className="pf-mb__cell" data-me={me || undefined}>{me ? (s.found ? "🎉" : "🧒") : gem ? "💎" : ""}</span>;
        })}
      </div>
      <p className="pf-mb__scene-state" data-testid="fs-mb-hot-state">
        {heat} {t("student.microbit.scene.hotCold.signal", { rssi, g: group ?? 0 })}
      </p>
      <Goal id="fs-mb-scene-goal" done={s.found} text={t("student.microbit.scene.hotCold.goal")} />
    </div>
  );
}
